import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import useIntersectionObserver from '../../features/useIntersectionObserver'
import useResizeObserver from '../../features/useResizeObserver'
import { ScrollObserverCore } from '../../features/ScrollObserver'

/**
 * Bronson FloatingBar component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-floating-bar.html
 *
 * @param {React.ReactNode} [children] - Optional content of the FloatingBar.
 * @param {string} [className] - Optional CSS classes on the component element.
 * @param {React.ReactNode} [target] - The target ref to track.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The FloatingBar component.
 * @constructor
 */
export function FloatingBar({ children, className, target, testId, ...otherProps }) {
  const floatingBarRef = useRef()

  /**
   * Track resizing on the FloatingBar ref and get current offset height.
   */
  useResizeObserver({
    ref: floatingBarRef,
    onResizeHandler: () => {
      const elementOffsetHeight = floatingBarRef?.current?.offsetHeight ?? ''
      document.documentElement.style.setProperty('--bron-sticky-offset', `${elementOffsetHeight}px`)
    },
  })

  /**
   * Track the target container.
   */
  useIntersectionObserver({
    ref: floatingBarRef,
    target,
    onIntersectionHandler: ScrollObserverCore.observeEntries,
    opts: {
      intersectionObserverOpts: { rootMargin: '0px', threshold: [0, 1] },
      classes: ScrollObserverCore.classes,
    },
  })

  const aClass = classNames('c-floating-bar', 'js-scroll-observer', className).trim()

  /**
   * Handle scroll to the desired element.
   * @param {HTMLElement} scrollToRef- The element to scroll to.
   */
  const scrollTo = (scrollToRef) => window.scrollTo(0, scrollToRef.current.offsetTop)

  return (
    <>
      {/* Navigation happens via `onClick`. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <a
        ref={floatingBarRef}
        data-testid={testId}
        className={aClass}
        data-scroll
        onClick={() => {
          scrollTo(target)
        }}
        {...otherProps}
      >
        <div className="c-floating-bar__inner">{children}</div>
      </a>
    </>
  )
}

FloatingBar.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  target: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}
